import { useEffect, useState } from "react";

// ---- Service worker registration ----
export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => console.debug("sw register failed", err?.message));
  });
}

async function swMessage(msg, onProgress) {
  const reg = await navigator.serviceWorker.ready;
  const sw = reg.active || navigator.serviceWorker.controller;
  if (!sw) throw new Error("no-sw");
  const id = Math.random().toString(36).slice(2);
  return new Promise((resolve) => {
    const handler = (e) => {
      if (e.data?.id !== id) return;
      if (e.data.type === "SAVE_PROGRESS") onProgress?.(e.data);
      else { navigator.serviceWorker.removeEventListener("message", handler); resolve(e.data); }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    sw.postMessage({ id, ...msg });
  });
}

export const OFFLINE_FLAG = "fab_offline_saved_at";
export const offlineSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "caches" in window;

// Collects everything the Guides + Case-study pages need and hands it to the SW.
export async function saveGuidesOffline({ guides, scenarios }, onProgress) {
  await import("@/pages/ScenarioArticle"); // make sure the case-study chunk is in the resource list
  const assets = performance.getEntriesByType("resource")
    .map((r) => r.name)
    .filter((n) => n.startsWith(window.location.origin + "/static/") || n.includes("fonts.googleapis") || n.includes("fonts.gstatic"));
  const urls = Array.from(new Set([
    "/", "/guides", "/learn/scenarios", "/manifest.json", "/logo-firealarmbt-light.png",
    ...assets,
    ...guides.flatMap((g) => [`/guides/${g.slug}`, g.hero, g.hero?.startsWith("/guide-hero/") ? g.hero.replace(/\.jpg$/, ".webp") : null]),
    ...scenarios.flatMap((s) => [`/learn/${s.slug}`, s.ogImage]),
  ].filter(Boolean)));
  const res = await swMessage({ type: "SAVE_OFFLINE", urls }, onProgress);
  if (res.failed < res.total) localStorage.setItem(OFFLINE_FLAG, new Date().toISOString());
  return res;
}

export async function clearOfflineGuides() {
  localStorage.removeItem(OFFLINE_FLAG);
  return swMessage({ type: "CLEAR_OFFLINE" });
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}

// ---- Install prompt (Android/Chrome native event; iOS gets manual instructions) ----
let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; listeners.forEach((l) => l()); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; localStorage.setItem("fab_app_installed", "1"); listeners.forEach((l) => l()); });
}
export const isStandalone = () => typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
export const isIos = () => typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export function useInstallPrompt() {
  const [, force] = useState(0);
  useEffect(() => { const l = () => force((n) => n + 1); listeners.add(l); return () => listeners.delete(l); }, []);
  const installed = isStandalone() || (typeof localStorage !== "undefined" && localStorage.getItem("fab_app_installed") === "1");
  const canPrompt = !!deferredPrompt;
  const ios = isIos();
  const available = !installed && (canPrompt || ios);
  const install = async () => {
    if (!deferredPrompt) return "manual";
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") deferredPrompt = null;
    listeners.forEach((l) => l());
    return outcome;
  };
  return { available, canPrompt, ios, installed, install };
}
